import { useState } from 'react';
import { motion } from 'framer-motion';
import { Participant } from '../App';
import { ProfileBubble } from './ProfileBubble';
import { ProfileOverlay } from './ProfileOverlay';

interface GroupChatProfileProps {
  groupName: string;
  participants: Participant[];
  conversationId?: number;
}





export function GroupChatProfile({ 
  groupName, 
  participants,
  conversationId
}: GroupChatProfileProps) {
  // Handle empty participants array
  const validParticipants = participants && participants.length > 0 ? participants : [
    { id: 'fallback-1', name: groupName, phoneNumber: '(555) 000-0000', role: 'Group Member' }
  ];
  
  const [selectedParticipant, setSelectedParticipant] = useState<Participant>(validParticipants[0]);

  const [isProfileOverlayOpen, setIsProfileOverlayOpen] = useState(false);



  // Apple-style organic positioning with variable sizes - constrained to avoid tags area
  const getParticipantPosition = (index: number, total: number) => {
    // Define positions that match the Apple reference image - organic placement with overlaps
    // Maximum vertical position is 63% to ensure bubbles don't interfere with tags area (starts at ~70%)
    const positions = [
      // Primary focal point - largest bubble, center-left
      { top: '40%', left: '35%', transform: 'translate(-50%, -50%)', zIndex: 15, size: 'w-24 h-24' },
      
      // Secondary - medium bubble, upper right with slight overlap
      { top: '25%', left: '58%', transform: 'translate(-50%, -50%)', zIndex: 12, size: 'w-20 h-20' },
      
      // Third - smaller bubble, lower right (moved up from 55% to stay clear)
      { top: '52%', left: '65%', transform: 'translate(-50%, -50%)', zIndex: 10, size: 'w-16 h-16' },
      
      // Fourth - medium-small, upper left
      { top: '28%', left: '22%', transform: 'translate(-50%, -50%)', zIndex: 9, size: 'w-18 h-18' },
      
      // Fifth - small, lower left (moved up from 58% to stay clear)
      { top: '55%', left: '18%', transform: 'translate(-50%, -50%)', zIndex: 8, size: 'w-14 h-14' },
      
      // Sixth - medium, center top
      { top: '18%', left: '42%', transform: 'translate(-50%, -50%)', zIndex: 11, size: 'w-19 h-19' },
      
      // Additional natural scattered positions for larger groups (all constrained to max 63%)
      { top: '45%', left: '68%', transform: 'translate(-50%, -50%)', zIndex: 7, size: 'w-15 h-15' },
      { top: '32%', left: '75%', transform: 'translate(-50%, -50%)', zIndex: 6, size: 'w-17 h-17' },
      { top: '60%', left: '45%', transform: 'translate(-50%, -50%)', zIndex: 5, size: 'w-16 h-16' },
      { top: '22%', left: '12%', transform: 'translate(-50%, -50%)', zIndex: 4, size: 'w-15 h-15' },
      { top: '50%', left: '82%', transform: 'translate(-50%, -50%)', zIndex: 3, size: 'w-14 h-14' },
      { top: '38%', left: '8%', transform: 'translate(-50%, -50%)', zIndex: 2, size: 'w-13 h-13' },
      
      // Extended positions for very large groups (7-12 participants) - all safely above tags
      { top: '63%', left: '28%', transform: 'translate(-50%, -50%)', zIndex: 13, size: 'w-14 h-14' },
      { top: '15%', left: '68%', transform: 'translate(-50%, -50%)', zIndex: 12, size: 'w-15 h-15' },
      { top: '58%', left: '75%', transform: 'translate(-50%, -50%)', zIndex: 11, size: 'w-13 h-13' },
      { top: '48%', left: '5%', transform: 'translate(-50%, -50%)', zIndex: 10, size: 'w-16 h-16' },
      { top: '62%', left: '58%', transform: 'translate(-50%, -50%)', zIndex: 9, size: 'w-15 h-15' },
      { top: '12%', left: '85%', transform: 'translate(-50%, -50%)', zIndex: 8, size: 'w-14 h-14' }
    ];
    
    return positions[index] || positions[positions.length - 1];
  };

  return (
    <motion.div 
      className="relative h-[450px] rounded-3xl overflow-hidden shadow-sm border border-gray-200/30 dark:border-gray-700/30 bg-white dark:bg-gray-900 hover:shadow-md"
      animate={{
        scale: selectedParticipant ? 1.02 : 1,
      }}
      transition={{
        type: "spring",
        stiffness: 200,
        damping: 25,
        duration: 0.3
      }}
    >
      {/* Clean iOS-style background with subtle circular gradient like reference */}
      <motion.div 
        className="absolute inset-0 bg-gradient-to-b from-gray-50/40 to-gray-100/30 dark:from-gray-800/40 dark:to-gray-900/30"
        animate={{
          opacity: selectedParticipant ? 0.8 : 1,
        }}
        transition={{ duration: 0.3 }}
      />
      <div className="absolute inset-0 bg-gradient-radial from-gray-100/20 dark:from-gray-700/20 via-transparent to-transparent" />
      
      {/* Header badges - moved slightly higher */}
      <div className="absolute top-5 left-5 z-20">
        <button
          onClick={() => setIsProfileOverlayOpen(true)}
          className={`group flex items-center gap-2 px-4 py-2 backdrop-blur-xl rounded-full transition-all duration-200 shadow-sm hover:shadow-md border ${
            isProfileOverlayOpen 
              ? 'bg-blue-500/90 hover:bg-blue-600/90 border-blue-400/50 text-white' 
              : 'bg-white/60 dark:bg-gray-800/60 hover:bg-white/80 dark:hover:bg-gray-800/80 border-gray-200/50 dark:border-gray-700/50'
          }`}
        >
          <svg className={`w-4 h-4 transition-colors ${
            isProfileOverlayOpen 
              ? 'text-white' 
              : 'text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-200'
          }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span className={`font-['SF_Pro_Text',_-apple-system,_sans-serif] text-[15px] font-[500] tracking-[-0.1px] transition-colors ${
            isProfileOverlayOpen 
              ? 'text-white' 
              : 'text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-200'
          }`}>
            {isProfileOverlayOpen ? selectedParticipant.name.split(' ')[0] : 'Profile'}
          </span>
        </button>
      </div>

      <div className="absolute top-5 right-5 z-20">
        <span className="font-['SF_Pro_Text',_-apple-system,_sans-serif] text-gray-500 dark:text-gray-400 text-[17px] font-[400] tracking-[-0.2px]">
          {validParticipants.length} member{validParticipants.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Apple-style organic profile layout with modular ProfileBubble components */}
      {/* Constrained container to prevent overlap with tags - increased bottom padding */}
      <div className="absolute inset-0 pt-12 pb-40">
        <div className="relative w-full h-full overflow-hidden">
          {/* Visual boundary indicators for development */}
          {import.meta.env.DEV && (
            <>
              {/* Safety boundary at 65% - no bubbles should go below this */}
              <div className="absolute left-0 right-0 h-px bg-red-300 opacity-40 pointer-events-none" style={{ top: '65%' }} />
              {/* Container bottom boundary */}
              <div className="absolute bottom-0 left-0 right-0 h-px bg-red-200 opacity-30 pointer-events-none" />
              {/* Tags area start indicator */}
              <div className="absolute left-0 right-0 h-px bg-yellow-300 opacity-40 pointer-events-none" style={{ bottom: '160px' }} />
            </>
          )}
          
          {validParticipants.slice(0, 12).map((participant, index) => {
            const position = getParticipantPosition(index, validParticipants.length);
            const isSelected = selectedParticipant.id === participant.id;
            const isAnySelected = selectedParticipant !== null;
            
            return (
              <ProfileBubble
                key={participant.id}
                participant={participant}
                position={position}
                isSelected={isSelected}
                isAnySelected={isAnySelected}
                conversationId={conversationId || 0}
                onClick={() => {
                  setSelectedParticipant(participant);
                  // Close overlay if open so it can re-open with new participant data
                  if (isProfileOverlayOpen) {
                    setIsProfileOverlayOpen(false);
                    // Small delay to ensure clean transition
                    setTimeout(() => setIsProfileOverlayOpen(true), 100);
                  }
                }}
              />
            );
          })}
        </div>
      </div>

      {/* Bottom content area - protected zone with clear visual boundaries */}
      <motion.div 
        className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-white/98 dark:from-gray-900/98 via-white/95 dark:via-gray-900/95 to-white/85 dark:to-gray-900/85 border-t border-white/50 dark:border-gray-700/50"
        animate={{
          y: selectedParticipant ? -2 : 0,
        }}
        transition={{ duration: 0.3 }}
      >


        {/* Selected participant info - compact spacing with enhanced details */}
        <motion.div 
          className="text-center space-y-1"
          key={selectedParticipant.id} // Re-trigger animation on participant change
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <div className="flex items-center justify-center gap-2">
            <h3 className="font-['SF_Pro_Display',_-apple-system,_sans-serif] text-gray-900 dark:text-gray-100 text-[28px] font-[600] tracking-[-0.4px]">
              {selectedParticipant.name}
            </h3>
            {/* Status indicator if available */}
            <motion.div 
              className="w-3 h-3 bg-green-500 rounded-full opacity-80"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
          
          {selectedParticipant.role && (
            <motion.p 
              className="font-['SF_Pro_Text',_-apple-system,_sans-serif] text-gray-600 dark:text-gray-400 text-[16px] font-[400] tracking-[-0.2px]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.3 }}
            >
              {selectedParticipant.role}
            </motion.p>
          )}
          
          <motion.p 
            className="font-['SF_Pro_Text',_-apple-system,_sans-serif] text-gray-500 dark:text-gray-400 text-[14px] font-[400] tracking-[-0.1px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.3 }}
          >
            {selectedParticipant.phoneNumber}
          </motion.p>
          
          {selectedParticipant.lastActive && (
            <motion.p 
              className="font-['SF_Pro_Text',_-apple-system,_sans-serif] text-gray-400 dark:text-gray-500 text-[13px] font-[400] tracking-[-0.1px]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.3 }}
            >
              Last active {selectedParticipant.lastActive.toLocaleDateString()}
            </motion.p>
          )}
        </motion.div>
      </motion.div>

      {/* Profile Overlay */}
      {isProfileOverlayOpen && (
        <ProfileOverlay
          participant={selectedParticipant}
          isOpen={isProfileOverlayOpen}
          onClose={() => setIsProfileOverlayOpen(false)}
          conversationId={conversationId}
        />
      )}
    </motion.div>
  );
}